import React from "react";
import { DarkMode, LightMode } from "@mui/icons-material";
import {
  ToggleButton,
  toggleButtonClasses,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Typography,
  useTheme,
} from "@mui/material";
import { useAppThemeSwitchContext } from "src/providers/ThemeProvider";

interface ThemeSwitchProps {
  collapsed?: boolean;
}

/**
 * Theme toggle component for light/dark mode switching.
 *
 * Shows text labels when expanded, icons only when collapsed.
 */
export function ThemeSwitch({ collapsed = false }: ThemeSwitchProps) {
  const theme = useTheme();
  const { mode, setPaletteMode } = useAppThemeSwitchContext();

  const toggleThemeMode = () => {
    if (mode === "light") {
      setPaletteMode("dark");
    }
    if (mode === "dark") {
      setPaletteMode("light");
    }
  };

  const setPaletteLight = () => setPaletteMode("light");
  const setPaletteDark = () => setPaletteMode("dark");

  return (
    <ToggleButtonGroup
      color="primary"
      value={mode}
      exclusive
      fullWidth
      size="small"
      sx={{
        width: "unset",
        alignSelf: "stretch",
        overflow: "hidden",
        height: collapsed ? 48 : 36,

        marginLeft: collapsed ? 0 : 1.5,
        marginRight: collapsed ? 0 : 1.5,
        marginTop: collapsed ? 0 : 0.75,
        marginBottom: collapsed ? 0 : 0.75,
        padding: collapsed ? 0 : theme.spacing(0.5),

        borderWidth: collapsed ? 0 : 1,
        borderStyle: "solid",
        borderColor: collapsed ? "transparent" : theme.palette.divider,
        borderRadius: collapsed ? theme.spacing(1) : theme.spacing(0.5),
        transition: theme.transitions.create(
          [
            "margin-left",
            "margin-right",
            "margin-top",
            "margin-bottom",
            "height",
            "border-width",
            "border-color",
            "padding",
            "border-radius",
          ],
          {
            easing: theme.transitions.easing.sharp,
            duration: collapsed
              ? theme.transitions.duration.leavingScreen
              : theme.transitions.duration.enteringScreen,
          }
        ),
        [`.${toggleButtonClasses.root}`]: {
          overflow: "clip",
          border: "none",
        },
        [`.${toggleButtonGroupClasses.grouped}`]: {
          "&:not(:first-of-type)": {
            marginLeft: 0,
            borderLeft: 0,
            borderTopLeftRadius: theme.spacing(0.5),
            borderBottomLeftRadius: theme.spacing(0.5),
            borderTopRightRadius: theme.spacing(0.5),
            borderBottomRightRadius: theme.spacing(0.5),
          },
          "&:not(:last-of-type)": {
            borderTopLeftRadius: theme.spacing(0.5),
            borderBottomLeftRadius: theme.spacing(0.5),
            borderTopRightRadius: theme.spacing(0.5),
            borderBottomRightRadius: theme.spacing(0.5),
          },
        },
      }}
    >
      <ToggleButton
        value="light"
        selected={collapsed ? false : mode === "light"}
        onClick={collapsed ? toggleThemeMode : setPaletteLight}
        sx={{
          color: "text.secondary",
        }}
      >
        {collapsed && (
          <React.Fragment>
            {mode === "light" && <DarkMode />}
            {mode === "dark" && <LightMode />}
          </React.Fragment>
        )}

        {!collapsed && <LightMode />}

        <Typography
          variant="button"
          textTransform="none"
          align="left"
          sx={{
            overflow: "clip",
            ml: collapsed ? 0 : 1,
            opacity: collapsed ? 0 : 1,
            width: collapsed ? 0 : undefined,
            transition: theme.transitions.create(["width", "opacity", "margin-left"], {
              easing: theme.transitions.easing.sharp,
              duration: collapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          Light
        </Typography>
      </ToggleButton>
      <ToggleButton
        value="dark"
        onClick={setPaletteDark}
        sx={{
          borderWidth: "none",
          color: "text.secondary",
          padding: collapsed ? 0 : undefined,
          width: collapsed ? 0 : undefined,
          opacity: collapsed ? 0 : 1,
          transition: theme.transitions.create(["width", "margin-left", "opacity", "padding"], {
            easing: theme.transitions.easing.sharp,
            duration: collapsed
              ? theme.transitions.duration.leavingScreen
              : theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <DarkMode />
        <Typography
          variant="button"
          textTransform="none"
          align="left"
          sx={{
            ml: 1,
            width: collapsed ? 0 : undefined,
            transition: theme.transitions.create(["width", "margin-left"], {
              easing: theme.transitions.easing.sharp,
              duration: collapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          Dark
        </Typography>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
